import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { defaultDatePickerStrings } from "@fluentui/react-datepicker-compat";
import { useToastGlobal } from "../../../stores/useToastGlobal";
import {
    useQueryGetAllAgencies,
    useQueryGetAllManagers,
    useQueryGetAllServices,
} from "../../../api/helpers/queries";
import { _api_export_pilotage_synthese } from "../api";
import { buildExportSyntheseSearchParams } from "../api/queries";
import {
    COLLABORATEURS,
    EXPORT_DETAIL_GROUPS,
    EXPORT_SYNTHESE_OPTIONS,
} from "../constants";

const COMMON_OPTIONS = { returnObjects: true, ns: "common" };

export const EXPORT_TABS = {
    synthese: "synthese",
    detail: "detail",
};

const buildDetailState = () =>
    EXPORT_DETAIL_GROUPS.reduce((acc, group) => {
        group.options.forEach((opt) => {
            acc[opt.key] = !!opt.checked;
        });
        return acc;
    }, {});

const buildDefaultValues = () => ({
    start_date: new Date(new Date().getFullYear(), 0, 1),
    end_date: new Date(),
    export_format: "xlsx",
    department_ids: [],
    agency_ids: [],
    manager_ids: [],
    compare_by: "department",
    sections: Object.fromEntries(
        EXPORT_SYNTHESE_OPTIONS.map((opt) => [opt.key, true])
    ),
});

const toItems = (list, getHeader) =>
    (list ?? []).map((item) => ({ key: item.id, header: getHeader(item) }));

const pad = (n) => String(n).padStart(2, "0");
export const formatDate = (d) =>
    d ? `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}` : "";

/** Logique de la modale "Exporter les données" */
const useExportDialog = () => {
    const { t } = useTranslation();
    const setShowToast = useToastGlobal((s) => s.setShow);

    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState(EXPORT_TABS.synthese);
    const [isExporting, setIsExporting] = useState(false);

    // période, format, périmètre & sections de la synthèse
    const form = useForm({ defaultValues: buildDefaultValues() });
    const [startDate, endDate, sections] = form.watch([
        "start_date",
        "end_date",
        "sections",
    ]);

    // options du périmètre (mêmes queries que la FilterBar)
    const services = useQueryGetAllServices();
    const agencies = useQueryGetAllAgencies();
    const managers = useQueryGetAllManagers();

    // onglet détail (maquette)
    const [collaborateurs, setCollaborateurs] = useState([]);
    const [detail, setDetail] = useState(buildDetailState);

    const localizedStrings = useMemo(
        () => ({
            ...defaultDatePickerStrings,
            days: t("temporal.days", COMMON_OPTIONS),
            shortDays: t("temporal.shortDays", COMMON_OPTIONS),
            months: t("temporal.months", COMMON_OPTIONS),
            shortMonths: t("temporal.shortMonths", COMMON_OPTIONS),
            goToToday: t("temporal.goToToday", COMMON_OPTIONS),
        }),
        [t]
    );

    const collaborateursDisponibles = useMemo(
        () =>
            COLLABORATEURS.map((c) => ({
                key: c.name,
                header: c.name,
                content: c.dept,
            })),
        []
    );

    const toggleDetail = (key) =>
        setDetail((prev) => ({ ...prev, [key]: !prev[key] }));

    const isGroupAllChecked = (group) =>
        group.options.every((opt) => detail[opt.key]);

    const toggleGroup = (group) => {
        const all = isGroupAllChecked(group);
        setDetail((prev) => {
            const next = { ...prev };
            group.options.forEach((opt) => {
                next[opt.key] = !all;
            });
            return next;
        });
    };

    const reset = () => {
        setTab(EXPORT_TABS.synthese);
        form.reset(buildDefaultValues());
        setCollaborateurs([]);
        setDetail(buildDetailState());
    };

    const close = () => {
        setOpen(false);
        reset();
    };

    const canExport =
        tab === EXPORT_TABS.synthese &&
        !!startDate &&
        !!endDate &&
        Object.values(sections).some(Boolean) &&
        !isExporting;

    const exportData = form.handleSubmit((values) => {
        setIsExporting(true);
        return _api_export_pilotage_synthese({
            searchParams: buildExportSyntheseSearchParams(values),
            exportFormat: values.export_format,
            cbOnEndDownload: (data, error, message) => {
                setIsExporting(false);
                if (error) {
                    setShowToast({
                        visible: true,
                        level: "danger",
                        content:
                            message ||
                            "Une erreur s'est produite lors de l'export",
                    });
                    return;
                }
                setShowToast({
                    visible: true,
                    level: "success",
                    content: "Export téléchargé",
                    auto_hide_timeout: 2400,
                });
                close();
            },
        });
    });

    return {
        dialog: { open, openDialog: () => setOpen(true), close },
        tab: { value: tab, set: setTab },
        form,
        periode: { localizedStrings, formatDate },
        perimetre: {
            departements: {
                items: toItems(services.data, (s) => s.name),
                isLoading: services.isLoading,
            },
            agences: {
                items: toItems(agencies.data, (a) => a.name),
                isLoading: agencies.isLoading,
            },
            managers: {
                items: toItems(
                    managers.data,
                    (m) => `${m.first_name} ${m.last_name}`
                ),
                isLoading: managers.isLoading,
            },
            collaborateurs,
            setCollaborateurs,
            collaborateursDisponibles,
        },
        colonnes: {
            detail,
            toggleDetail,
            isGroupAllChecked,
            toggleGroup,
        },
        exportData,
        canExport,
        isExporting,
    };
};

export default useExportDialog;
